import { createSelector } from 'reselect';

import { __ } from '../../../../i18n/l10n';
import { getSportIdFromProps } from '../../../../selectors/_propsGetters_';
import {
  selectEntitiesMatchesEntityCompetitor1Name,
  selectEntitiesMatchesEntityCompetitor2Name,
  selectEntitiesMatchesEntityStatus,
} from '../../../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from '../../../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import {
  makeComplexeMatchBetsSelector,
  selectIsMatchMMLoading,
  makeMatchCompetitorsSelector,
  selectMatchViewMode,
} from '../../../../selectors/match.selector';
import BetBuilder from '../../../../states/betbuilder';
import { BetTypes, type BetType } from '../../../../types/bet';
import { MarketIds, type MarketId } from '../../../../types/market';
import { type MatchStatus } from '../../../../types/match';
import { getFilterIdFromParams } from '../../../../types/matchBetFilters';
import { type MatchViewMode } from '../../../../types/matchView';
import { type Selector, type SelectorMaker } from '../../../../types/selectors';
import { SportIds, type SportId } from '../../../../types/sports';
import { MatchBetFilter } from '../../../../utils/contexts';
import { createShallowEqualSelector } from '../../../../utils/reselect';
import {
  LineUpFormationsBySportId,
  SportsWithFixedFormation,
  SportsWithNoTab,
  SportsWithRaceTrackUI,
} from '../../constants/runtime';
import { marketIdWithSpecifiers } from '../../selectors/lineUp.selector';
import { getAllUniqueSliderValues } from '../../utils';

const emptyArray: [] = [];

const sortedValidBetTypes: Partial<Record<SportId, Array<BetType>>> = {
  [SportIds.FORMULA_1]: [
    BetTypes.RACE_WINNER,
    BetTypes.RACE_TOP_3,
    BetTypes.RACE_TOP_6,
    BetTypes.RACE_TOP_10,
    BetTypes.FORMULA_1_WINNER,
    BetTypes.FORMULA_1_TOP_2,
    BetTypes.FORMULA_1_TOP_3,
    BetTypes.FORMULA_1_TOP_6,
    BetTypes.FORMULA_1_TOP_10,
    BetTypes.FORMULA_1_FASTEST_LAP,
  ],
  [SportIds.MOTO]: [
    BetTypes.RACE_WINNER,
    BetTypes.RACE_TOP_3,
    BetTypes.RACE_TOP_6,
    BetTypes.RACE_TOP_10,
    BetTypes.MOTO_WINNER,
    BetTypes.MOTO_TOP_3,
    BetTypes.MOTO_TOP_6,
    BetTypes.MOTO_TOP_10,
  ],
};

const sortedValidMarketIds: Partial<Record<SportId, Array<MarketId>>> = {
  [SportIds.SOCCER]: [
    MarketIds.MARKET_ID_OVERTIME_NEXT_SCORER,
    MarketIds.MARKET_ID_ANYTIME_GOALSCORER,
    MarketIds.MARKET_ID_NEXT_GOALSCORER,
    MarketIds.MARKET_ID_MULTIPLE_GOALSCORER,
    MarketIds.MARKET_ID_SCORE_AND_WIN,
    MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_1_HT,
    MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_2_HT,
    MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_BOTH_HT,
    MarketIds.MARKET_ID_FIRST_GOALSCORER,
    MarketIds.MARKET_ID_LAST_GOALSCORER_2,
    MarketIds.MARKET_ID_LAST_GOALSCORER,
    MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_15MINUTES,
    MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_10MINUTES,
    MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_1_HT,
    MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_2_HT,
    MarketIds.MARKET_ID_SOCCER_SCORER_OVERTIME,
    MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_OVERTIME,
    MarketIds.MARKET_ID_SOCCER_SCORER_OVERTIME_1_HT,
    MarketIds.MARKET_ID_SOCCER_SCORER_OVERTIME_2_HT,
  ],
  [SportIds.RUGBY]: [
    MarketIds.MARKET_ID_ANYTIME_GOALSCORER,
    MarketIds.MARKET_ID_NEXT_GOALSCORER,
    MarketIds.MARKET_ID_MULTIPLE_GOALSCORER,
    MarketIds.MARKET_ID_FIRST_GOALSCORER,
  ],
  [SportIds.RUGBY_LEAGUE]: [
    MarketIds.MARKET_ID_ANYTIME_GOALSCORER,
    MarketIds.MARKET_ID_NEXT_GOALSCORER,
    MarketIds.MARKET_ID_MULTIPLE_GOALSCORER,
    MarketIds.MARKET_ID_FIRST_GOALSCORER,
  ],
  [SportIds.BASKETBALL]: [
    MarketIds.MARKET_ID_PLAYER_POINTS,
    MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_REBOUND_ASSIST,
    MarketIds.MARKET_ID_PLAYER_REBOUND,
    MarketIds.MARKET_ID_PLAYER_PASS,
    MarketIds.MARKET_ID_PLAYER_3POINTS,
    MarketIds.MARKET_ID_PLAYER_INTERCEPT,
    MarketIds.MARKET_ID_PLAYER_BLOCK,
    MarketIds.MARKET_ID_PLAYER_LOST_BALL,
    MarketIds.MARKET_ID_TOTAL_PLAYER_ASSIST_REBOUND,
    MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_ASSIST,
    MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_REBOUND,
    MarketIds.MARKET_ID_TOTAL_PLAYER_INTERCEPT_BLOCK,
  ],
  [SportIds.ICEHOCKEY]: [
    MarketIds.MARKET_ID_ICE_HOCKEY_ANYTIME_GOALSCORER,
    MarketIds.MARKET_ID_ICE_HOCKEY_MULTI_GOALSCORER,
    MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_FIRST_PERIOD,
    MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_SECOND_PERIOD,
    MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_THIRD_PERIOD,
    MarketIds.MARKET_ID_NEXT_GOALSCORER,
  ],
};

/*
All keys for i18n:
__('!short:Next')
__('!short:2 or more')
__('!short:3 or more')
__('!short:4 or more')
__('!short:First')
__('!short:Last')
__('!short:1st half - 2 or more')
__('!short:1st half - 3 or more')
__('!short:1st half - 4 or more')
__('!short:2nd half - 2 or more')
__('!short:2nd half - 3 or more')
__('!short:2nd half - 4 or more')
__('!short:Overtime - 2 or more')
__('Points')
__('PRA')
__('Rebounds')
__('Assists')
__('Three-pointers')
__('Steals')
__('Blocks')
__('Turnovers')
__('Assists+rebounds')
__('Points+assists')
__('Points+rebounds')
__('Steals+blocks')
__('!short:Anytime goalscorer')
__('!short:Double')
__('!short:Hat-trick')
__('!short:1st period - Scorer')
__('!short:2nd period - Scorer')
__('!short:3rd period - Scorer')
*/

const shortedBetTitleByMarketIdAndSpecifier: Partial<Record<MarketId | BetType, string | Record<string, string>>> = {
  [MarketIds.MARKET_ID_NEXT_GOALSCORER]: '!short:Next',
  [MarketIds.MARKET_ID_MULTIPLE_GOALSCORER]: {
    'goals=2': '!short:2 or more',
    'goals=3': '!short:3 or more',
    'goals=4': '!short:4 or more',
  },
  [MarketIds.MARKET_ID_FIRST_GOALSCORER]: '!short:First',
  [MarketIds.MARKET_ID_LAST_GOALSCORER_2]: '!short:Last',
  [MarketIds.MARKET_ID_LAST_GOALSCORER]: '!short:Last',
  [MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_1_HT]: {
    'goals=2': '!short:1st half - 2 or more',
    'goals=3': '!short:1st half - 3 or more',
    'goals=4': '!short:1st half - 4 or more',
  },
  [MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_2_HT]: {
    'goals=2': '!short:2nd half - 2 or more',
    'goals=3': '!short:2nd half - 3 or more',
    'goals=4': '!short:2nd half - 4 or more',
  },
  [MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_OVERTIME]: {
    'goals=2': '!short:Overtime - 2 or more',
  },
  [MarketIds.MARKET_ID_PLAYER_POINTS]: 'Points',
  [MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_REBOUND_ASSIST]: 'PRA',
  [MarketIds.MARKET_ID_PLAYER_REBOUND]: 'Rebounds',
  [MarketIds.MARKET_ID_PLAYER_PASS]: 'Assists',
  [MarketIds.MARKET_ID_PLAYER_3POINTS]: 'Three-pointers',
  [MarketIds.MARKET_ID_PLAYER_INTERCEPT]: 'Steals',
  [MarketIds.MARKET_ID_PLAYER_BLOCK]: 'Blocks',
  [MarketIds.MARKET_ID_PLAYER_LOST_BALL]: 'Turnovers',
  [MarketIds.MARKET_ID_TOTAL_PLAYER_ASSIST_REBOUND]: 'Assists+rebounds',
  [MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_ASSIST]: 'Points+assists',
  [MarketIds.MARKET_ID_TOTAL_PLAYER_POINTS_REBOUND]: 'Points+rebounds',
  [MarketIds.MARKET_ID_TOTAL_PLAYER_INTERCEPT_BLOCK]: 'Steals+blocks',
  [MarketIds.MARKET_ID_ICE_HOCKEY_ANYTIME_GOALSCORER]: '!short:Anytime goalscorer',
  [MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_FIRST_PERIOD]: '!short:1st period - Scorer',
  [MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_SECOND_PERIOD]: '!short:2nd period - Scorer',
  [MarketIds.MARKET_ID_ICE_HOCKEY_SCORER_THIRD_PERIOD]: '!short:3rd period - Scorer',
};

type LineUpMainViewPropsType = {
  tabs: Array<{ id: string; label: string; disabled: boolean }>;
  filters: Array<MatchBetFilter>;
  matchStatus?: MatchStatus;
  matchViewMode: MatchViewMode;
  isMMLoading: boolean;
};

const makeLineUpMainViewPropsSelector: SelectorMaker<LineUpMainViewPropsType> = () => {
  const selectMatchFormations: Selector<string | Array<string> | undefined> = createShallowEqualSelector(
    [makeMatchCompetitorsSelector(), getSportIdFromProps],
    (competitors, sportId) => {
      if (sportId && SportsWithFixedFormation.indexOf(sportId) !== -1) {
        return [LineUpFormationsBySportId[sportId]];
      }
      return competitors.map((k) => k?.formationType).filter((k): k is string => Boolean(k));
    },
  );

  const selectFilters: Selector<Array<MatchBetFilter>> = createSelector(
    [makeComplexeMatchBetsSelector(), selectEntitiesOutcomes, getSportIdFromProps],
    (complexeMatchBets, outcomes, sportId) => {
      if (!sportId) {
        return emptyArray;
      }

      const resultMap: Record<string, MatchBetFilter> = {};

      let sortedValidFilteringIdsMap = [];
      let fieldTarget: 'marketId' | 'betType' = 'marketId';

      if (SportsWithRaceTrackUI.indexOf(sportId) !== -1) {
        fieldTarget = 'betType';
        sortedValidFilteringIdsMap = sortedValidBetTypes[sportId] ?? [];
      } else {
        sortedValidFilteringIdsMap = sortedValidMarketIds[sportId] ?? [];
      }

      for (let i = 0; i < sortedValidFilteringIdsMap.length; i++) {
        const filteringId = sortedValidFilteringIdsMap[i];
        const complexeMatchBetsByMarketId = complexeMatchBets.filter(
          (complexeMatchBet) => complexeMatchBet?.bet[fieldTarget] === filteringId,
        );

        if (complexeMatchBetsByMarketId.length) {
          const sliderValues = getAllUniqueSliderValues(complexeMatchBetsByMarketId, 'total', outcomes);
          for (const matchBetByMarketId of complexeMatchBetsByMarketId) {
            const {
              bet: { specialBetValue, betTitle },
              isMultichoice,
            } = matchBetByMarketId;
            const shouldInsertSBV = specialBetValue && marketIdWithSpecifiers.find((m) => m === filteringId);

            const id = getFilterIdFromParams({
              marketId: fieldTarget === 'marketId' ? filteringId : undefined,
              betType: fieldTarget === 'betType' ? filteringId : undefined,
              sbv: specialBetValue && shouldInsertSBV ? specialBetValue : undefined,
              isFromMultichoice: isMultichoice,
            });

            if (id) {
              const shortedLabelByMarketId = shortedBetTitleByMarketIdAndSpecifier[filteringId];

              let label = betTitle;

              if (typeof shortedLabelByMarketId === 'string') {
                label = __(shortedLabelByMarketId);
              } else if (typeof shortedLabelByMarketId === 'object' && Object.keys(shortedLabelByMarketId).length) {
                const shortedLabelBySpecifier = specialBetValue ? shortedLabelByMarketId[specialBetValue] : undefined;

                if (shortedLabelBySpecifier) {
                  label = __(shortedLabelBySpecifier);
                }
              }

              if (!Object.keys(resultMap).includes(id)) {
                resultMap[id] = {
                  id,
                  label: isMultichoice ? BetBuilder.addBetTitlePrefix(label) : label,
                  position: Object.keys(resultMap).length,
                  sliderValues: sliderValues.length ? sliderValues : undefined,
                };
              }
            }
          }
        }
      }

      return Object.keys(resultMap).map((key) => resultMap[key]);
    },
  );

  const selectTabs: Selector<Array<{ id: string; label: string; disabled: boolean }>> = createSelector(
    [
      selectEntitiesMatchesEntityCompetitor1Name,
      selectEntitiesMatchesEntityCompetitor2Name,
      selectMatchFormations,
      getSportIdFromProps,
    ],
    (competitor1Name, competitor2Name, matchFormations, sportId) => {
      if (!sportId || !matchFormations) {
        return emptyArray;
      }

      if (sportId === SportIds.ICEHOCKEY) {
        return [
          { id: '1', label: __('Line %1$s', 1), disabled: false },
          { id: '2', label: __('Line %1$s', 2), disabled: false },
          { id: '3', label: __('Line %1$s', 3), disabled: false },
          { id: '4', label: __('Line %1$s', 4), disabled: false },
        ];
      }
      if (SportsWithNoTab.indexOf(sportId) !== -1) {
        return emptyArray;
      }
      return [
        { id: '1', label: competitor1Name ?? '', disabled: !matchFormations[0] },
        { id: '2', label: competitor2Name ?? '', disabled: !matchFormations[1] },
      ];
    },
  );

  return createSelector(
    [selectTabs, selectFilters, selectEntitiesMatchesEntityStatus, selectMatchViewMode, selectIsMatchMMLoading],
    (tabs, filters, matchStatus, matchViewMode, isMMLoading) => ({
      tabs,
      filters,
      matchStatus,
      matchViewMode,
      isMMLoading,
    }),
  );
};

export default makeLineUpMainViewPropsSelector;
